<template>
  <div class="ec-page">
    <!-- Loading -->
    <div
      v-if="loadingData"
      class="ec-loading"
    >
      <div class="ec-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading contact…
      </p>
    </div>

    <template v-else>
      <!-- Hero -->
      <section class="ec-hero">
        <div class="ec-hero__glow" />
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12">
          <div class="d-flex align-items-center">
            <div
              class="ec-hero__avatar"
              :style="{ background: avatarColor }"
            >
              {{ initials(delegator.name) }}
            </div>
            <div class="ml-1">
              <h2 class="mb-0 ec-hero__title">
                {{ delegator.name || 'Edit Contact' }}
              </h2>
              <p class="mb-0 ec-hero__subtitle">
                <feather-icon
                  icon="HashIcon"
                  size="13"
                />
                {{ $route.params.id }} · Update contact details &amp; assets
              </p>
            </div>
          </div>
          <button
            type="button"
            class="ec-btn ec-btn--ghost"
            @click="goBack"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="16"
            />
            Back
          </button>
        </div>
      </section>

      <!--
        Read-only for everyone but admin. The write endpoint
        (POST delegator/signed/{id}) is ERP-owned and carries role:admin, so a
        marketer could previously fill this whole form and then be told
        "Access Denied" on save. Say it up front instead.
      -->
      <div
        v-if="canEdit === false"
        class="alert alert-warning d-flex align-items-center mb-2"
        role="alert"
      >
        <feather-icon
          icon="LockIcon"
          size="18"
          class="mr-1"
        />
        <div>
          You can view this contact, but only an administrator can change its
          details. Ask an admin, or edit the CRM-side information instead.
        </div>
      </div>

      <validation-observer
        ref="infoRules"
        v-slot="{ invalid }"
        tag="form"
        @submit.prevent
      >
        <div class="ec-grid">
          <!-- Basic info card -->
          <section class="ec-card ec-card--span2">
            <header class="ec-card__head">
              <span class="ec-card__head-icon ec-card__head-icon--primary">
                <feather-icon
                  icon="UserIcon"
                  size="18"
                />
              </span>
              <div>
                <h3 class="ec-card__title">
                  Basic Information
                </h3>
                <p class="ec-card__subtitle">
                  Name, email and phone number of the contact.
                </p>
              </div>
            </header>

            <div class="ec-card__body">
              <div class="ec-field-grid">
                <!-- Name -->
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  class="ec-field"
                >
                  <label class="ec-label">
                    <feather-icon
                      icon="UserIcon"
                      size="14"
                    />
                    Name
                  </label>
                  <div class="ec-input-wrap">
                    <input
                      v-model="delegator.name"
                      type="text"
                      class="ec-input"
                      :class="{ 'ec-input--invalid': validationContext.errors.length }"
                      placeholder="Contact name"
                    >
                  </div>
                  <small
                    v-if="validationContext.errors.length"
                    class="ec-error"
                  >{{ validationContext.errors[0] }}</small>
                </validation-provider>

                <!-- Email -->
                <validation-provider
                  #default="validationContext"
                  name="email"
                  class="ec-field"
                >
                  <label class="ec-label">
                    <feather-icon
                      icon="MailIcon"
                      size="14"
                    />
                    Email
                  </label>
                  <div class="ec-input-wrap">
                    <input
                      v-model="delegator.email"
                      type="text"
                      class="ec-input"
                      :class="{ 'ec-input--invalid': validationContext.errors.length }"
                      placeholder="name@example.com"
                    >
                  </div>
                  <small
                    v-if="validationContext.errors.length"
                    class="ec-error"
                  >{{ validationContext.errors[0] }}</small>
                </validation-provider>

                <!-- Phone -->
                <validation-provider
                  #default="validationContext"
                  name="phone"
                  class="ec-field"
                >
                  <label class="ec-label">
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                    />
                    Phone
                  </label>
                  <div class="ec-input-wrap">
                    <input
                      v-model="delegator.phone"
                      type="text"
                      class="ec-input"
                      :class="{ 'ec-input--invalid': validationContext.errors.length }"
                      placeholder="+966 5x xxx xxxx"
                    >
                  </div>
                  <small
                    v-if="validationContext.errors.length"
                    class="ec-error"
                  >{{ validationContext.errors[0] }}</small>
                </validation-provider>

                <!-- Is Broker -->
                <div class="ec-field">
                  <label class="ec-label">
                    <feather-icon
                      icon="BriefcaseIcon"
                      size="14"
                    />
                    Broker
                  </label>
                  <label class="ec-toggle">
                    <input
                      v-model="delegator.is_broker"
                      type="checkbox"
                      class="ec-toggle__input"
                      :true-value="1"
                      :false-value="0"
                    >
                    <span class="ec-toggle__track">
                      <span class="ec-toggle__thumb" />
                    </span>
                    <span class="ec-toggle__text">
                      {{ delegator.is_broker === 1 ? 'Yes — contact is a broker' : 'No — not a broker' }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          <!-- Signature card -->
          <section class="ec-card">
            <header class="ec-card__head">
              <span class="ec-card__head-icon ec-card__head-icon--info">
                <feather-icon
                  icon="Edit3Icon"
                  size="18"
                />
              </span>
              <div>
                <h3 class="ec-card__title">
                  Signature
                </h3>
                <p class="ec-card__subtitle">
                  PNG only.
                </p>
              </div>
            </header>

            <div class="ec-card__body">
              <div class="ec-asset">
                <div class="ec-asset__preview">
                  <img
                    v-if="signaturePreview"
                    :src="signaturePreview"
                    alt="Signature preview"
                  >
                  <span
                    v-else
                    class="ec-asset__placeholder"
                  >
                    <feather-icon
                      icon="ImageIcon"
                      size="26"
                    />
                    No signature
                  </span>
                </div>

                <label
                  class="ec-drop"
                  :class="{ 'ec-drop--error': fileErrors.signature }"
                >
                  <input
                    type="file"
                    accept="image/png,.png"
                    class="ec-drop__input"
                    @change="onFileChange($event, 'signature')"
                  >
                  <feather-icon
                    icon="UploadCloudIcon"
                    size="20"
                  />
                  <span class="ec-drop__text">
                    <strong>Click to upload</strong> a PNG signature
                  </span>
                </label>
                <small
                  v-if="fileErrors.signature"
                  class="ec-error"
                >{{ fileErrors.signature }}</small>
              </div>
            </div>
          </section>

          <!-- Stamp card -->
          <section class="ec-card">
            <header class="ec-card__head">
              <span class="ec-card__head-icon ec-card__head-icon--success">
                <feather-icon
                  icon="AwardIcon"
                  size="18"
                />
              </span>
              <div>
                <h3 class="ec-card__title">
                  Stamp
                </h3>
                <p class="ec-card__subtitle">
                  PNG only.
                </p>
              </div>
            </header>

            <div class="ec-card__body">
              <div class="ec-asset">
                <div class="ec-asset__preview">
                  <img
                    v-if="stampPreview"
                    :src="stampPreview"
                    alt="Stamp preview"
                  >
                  <span
                    v-else
                    class="ec-asset__placeholder"
                  >
                    <feather-icon
                      icon="ImageIcon"
                      size="26"
                    />
                    No stamp
                  </span>
                </div>

                <label
                  class="ec-drop"
                  :class="{ 'ec-drop--error': fileErrors.stamp }"
                >
                  <input
                    type="file"
                    accept="image/png,.png"
                    class="ec-drop__input"
                    @change="onFileChange($event, 'stamp')"
                  >
                  <feather-icon
                    icon="UploadCloudIcon"
                    size="20"
                  />
                  <span class="ec-drop__text">
                    <strong>Click to upload</strong> a PNG stamp
                  </span>
                </label>
                <small
                  v-if="fileErrors.stamp"
                  class="ec-error"
                >{{ fileErrors.stamp }}</small>
              </div>
            </div>
          </section>
        </div>

        <!-- Action bar -->
        <div class="ec-actionbar">
          <button
            type="button"
            class="ec-btn ec-btn--ghost"
            @click="goBack"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
            Cancel
          </button>
          <button
            type="button"
            class="ec-btn ec-btn--primary"
            :disabled="canEdit === false || loader || invalid || Object.values(errors).length > 0 || hasFileErrors"
            :title="canEdit === false ? 'Only an admin can change contact details' : null"
            @click="addDelegator()"
          >
            <feather-icon
              v-if="!loader"
              icon="SaveIcon"
              size="16"
            />
            <span
              v-else
              class="ec-btn__spinner"
            />
            {{ loader ? 'Saving…' : 'Save Changes' }}
          </button>
        </div>
      </validation-observer>
    </template>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import axios from 'axios'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #00cfe8, #5be0ef)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #ff9f43, #ffb976)',
  'linear-gradient(135deg, #ea5455, #f08182)',
  'linear-gradient(135deg, #5a8dee, #7aa5f2)',
]

export default {
  name: 'EditContactCRM',
  data() {
    return {
      required,
      loader: false,
      loadingData: false,
      /**
       * Whether this user may actually SAVE.
       *
       * The write endpoint (POST delegator/signed/{id}) is ERP-owned and restricted
       * to `role:admin`. A marketer could open this form, fill it in, press Save and
       * be told "Access Denied" — a form that accepts your work and then refuses it
       * is worse than one that is honestly read-only.
       *
       * Null until crm/me answers, so nothing flickers between states on load.
       */
      canEdit: null,
      errors: {},
      signatureFile: null,
      stampFile: null,
      signaturePreviewUrl: '',
      stampPreviewUrl: '',
      fileErrors: {
        signature: '',
        stamp: '',
      },
    }
  },
  setup() {
    const { getValidationState } = formValidation()
    const delegator = ref({})

    return {
      getValidationState,
      delegator,
    }
  },
  computed: {
    signaturePreview() {
      return this.signaturePreviewUrl || this.delegator.signature || null
    },
    stampPreview() {
      return this.stampPreviewUrl || this.delegator.stamp || null
    },
    hasFileErrors() {
      return Boolean(this.fileErrors.signature || this.fileErrors.stamp)
    },
    avatarColor() {
      const id = Number(this.$route.params.id) || (this.delegator.name ? this.delegator.name.length : 0)
      return AVATAR_COLORS[id % AVATAR_COLORS.length]
    },
  },
  beforeDestroy() {
    this.revokePreviewUrl('signature')
    this.revokePreviewUrl('stamp')
  },
  mounted() {
    this.showDelegator()
    this.loadAccess()
  },
  methods: {
    initials(name) {
      if (!name) return '??'
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    goBack() {
      this.$router.push({ name: 'crm-system-contacts' }).catch(() => {})
    },
    isPngFile(file) {
      if (!file) return false
      const isPngType = file.type === 'image/png'
      const isPngName = /\.png$/i.test(file.name)
      return isPngType || isPngName
    },
    revokePreviewUrl(field) {
      const previewKey = `${field}PreviewUrl`
      if (this[previewKey]) {
        URL.revokeObjectURL(this[previewKey])
        this[previewKey] = ''
      }
    },
    onFileChange(event, field) {
      const file = event.target.files?.[0] || null
      this.fileErrors[field] = ''
      this.revokePreviewUrl(field)

      if (!file) {
        this[`${field}File`] = null
        return
      }

      if (!this.isPngFile(file)) {
        this.fileErrors[field] = 'Only PNG files are allowed.'
        this[`${field}File`] = null
        const input = event.target
        if (input) input.value = ''
        return
      }

      this[`${field}File`] = file
      this[`${field}PreviewUrl`] = URL.createObjectURL(file)
    },
    async showDelegator() {
      if (this.$route.params.id) {
        this.loadingData = true
        await axios.get(`delegator/signed/${this.$route.params.id}`).then(res => {
          const data = decryptData(res.data.payload).data.delegator
          this.delegator = {
            ...data,
            is_broker: [true, 1, '1', 'true'].includes(data.is_broker) ? 1 : 0,
          }
        })
      } else {
        this.loadingData = false
        return false
      }
      this.loadingData = false
      return true
    },
    addDelegator() {
      if (this.$route.params.id) {
        this.loader = true
        const formData = new FormData()

        const skipKeys = ['signature', 'stamp']
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const key in this.delegator) {
          if (!skipKeys.includes(key)) {
            const value = this.delegator[key]
            if (value != null && typeof value !== 'object') {
              let formValue = value
              if (key === 'is_broker') {
                formValue = value ? 1 : 0
              }
              formData.append(key, formValue)
            }
          }
        }

        if (this.signatureFile) {
          formData.append('signature', this.signatureFile)
        }
        if (this.stampFile) {
          formData.append('stamp', this.stampFile)
        }

        axios.post(`delegator/signed/${this.$route.params.id}`, formData).then(res => {
          if (res.status === 200) {
            this.$router.push({ name: 'crm-system-contacts' })
            this.$toasted.show('Updated Successfully', {
              position: 'top-center',
              duration: 3000,
            })
          }
        }).catch(error => {
          if (error.response.status) {
            let message
            if (error.response.status === 403) {
              message = 'Access Denied. You do not have permission to perform this action.'
            } else {
              message = decryptData(error.response.data.payload).message
            }
            this.$toast({
              component: ToastificationContent,
              props: {
                title: message,
                icon: 'BellIcon',
                variant: 'error',
              },
            })
          }
        }).finally(() => {
          this.loader = false
        })
      }
    },
  },
}
</script>

<style lang="scss" src="./edit-contact-crm.scss" scoped></style>
